import { articles } from "@/mocks/articles";
import { videos } from "@/mocks/videos";
import { topics } from "@/mocks/topics";
import { categories, categoryMap } from "@/mocks/categories";
import type { Article, Category, CategoryId, Topic, Video } from "@/types/content";

export { articles, videos, topics, categories, categoryMap };

export const getCategory = (id?: string): Category | undefined =>
  id ? categoryMap[id] : undefined;

export const getArticle = (id?: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getVideo = (id?: string): Video | undefined =>
  videos.find((item) => item.id === id);

export const getTopic = (id?: string): Topic | undefined =>
  topics.find((item) => item.id === id);

export const categoryArticles = (categoryId: string): Article[] =>
  articles.filter((item) => item.category === categoryId);

export const categoryVideos = (categoryId: string): Video[] =>
  videos.filter((item) => item.category === categoryId);

export const relatedArticles = (article: Article, limit = 5): Article[] =>
  articles
    .filter((item) => item.id !== article.id && item.category === article.category)
    .concat(articles.filter((item) => item.id !== article.id && item.category !== article.category))
    .slice(0, limit);

export const relatedVideos = (video: Video, limit = 4): Video[] =>
  videos
    .filter((item) => item.id !== video.id && item.category === video.category)
    .concat(videos.filter((item) => item.id !== video.id && item.category !== video.category))
    .slice(0, limit);

export const latestArticles = (limit = 6): Article[] =>
  [...articles].sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);

export const popularArticles = (limit = 5): Article[] =>
  [...articles].sort((a, b) => b.views - a.views).slice(0, limit);

export const topicArticles = (topic: Topic): Article[] =>
  topic.articleIds
    .map((id) => getArticle(id))
    .filter((item): item is Article => Boolean(item));

export const categoryTags = (categoryId: CategoryId): string[] => {
  const pool = [...categoryArticles(categoryId).map((a) => a.tags), ...categoryVideos(categoryId).map((v) => v.tags)].flat();
  return Array.from(new Set(pool));
};

export const formatViews = (views: number): string => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
};

export const formatDate = (date: string, style: "dot" | "dash" = "dot"): string => {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return style === "dot" ? parts.join(".") : parts.join("-");
};

export interface SearchResult {
  articles: Article[];
  videos: Video[];
}

export const searchContent = (rawKeyword: string): SearchResult => {
  const keyword = rawKeyword.trim().toLowerCase();
  if (!keyword) return { articles: [], videos: [] };
  const match = (text: string) => text.toLowerCase().includes(keyword);
  return {
    articles: articles.filter(
      (item) =>
        match(item.title) ||
        match(item.subtitle) ||
        match(item.summary) ||
        item.tags.some(match) ||
        match(item.author.name) ||
        (categoryMap[item.category]?.name ?? "").includes(keyword),
    ),
    videos: videos.filter(
      (item) =>
        match(item.title) ||
        match(item.summary) ||
        item.tags.some(match) ||
        match(item.author.name) ||
        (categoryMap[item.category]?.name ?? "").includes(keyword),
    ),
  };
};

export const suggestionList = (rawKeyword: string, limit = 6): Article[] => {
  const keyword = rawKeyword.trim().toLowerCase();
  if (!keyword) return [];
  return articles
    .filter(
      (item) => item.title.toLowerCase().includes(keyword) || item.tags.some((tag) => tag.toLowerCase().includes(keyword)),
    )
    .slice(0, limit);
};